import { useQuery } from '@tanstack/react-query';
import api from '@/api/axios';

/**
 * Hook for fetching users list with pagination and advanced filtering.
 * Now handles both standard table view and search via POST requests.
 */
export const useUsersQuery = ({
  schemeId,
  page,
  search,
  filterGift,
  filterState,
  filterStatus,
  filterSlab,
  filterMobile,
  filterOutlet,
  filterPincode,
  dateFrom,
  dateTo,
  perPage
}) => {
  return useQuery({
    queryKey: [
      'scheme_users', 
      schemeId, 
      page, 
      search, 
      filterGift, 
      filterState, 
      filterStatus, 
      filterSlab, 
      filterMobile, 
      filterOutlet, 
      filterPincode, 
      dateFrom, 
      dateTo
    ],
    queryFn: async () => {
      const isSearching = search.trim().length > 0;
      const endpoint = isSearching ? 'search_users.php' : 'users_table.php';
      
      // Construct the payload based on whether we are searching or filtering
      const payload = {
        scheme_id: schemeId,
        ...(isSearching 
          ? { 
              q: search 
            } 
          : { 
              page, 
              limit: perPage,
              mobile_no: filterMobile || undefined,
              outlet_name: filterOutlet || undefined,
              gift: filterGift || undefined,
              state: filterState || undefined,
              status: filterStatus || undefined,
              slab: filterSlab || undefined,
              pincode: filterPincode || undefined,
              date_from: dateFrom || undefined,
              date_to: dateTo || undefined,
            }
        ),
      };

      // Both search_users.php and users_table.php now use php://input (POST)
      const response = await api.post(endpoint, payload);
      return response.data;
    },
    enabled: !!schemeId,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook for fetching filter dropdown options (States, Gifts, Slabs)
 * Uses POST to send scheme_id in the request body.
 */
export const useFilterOptions = (schemeId) => {
  return useQuery({
    queryKey: ['filter_options', schemeId],
    queryFn: async () => {
      const res = await api.post('filter_options.php', { 
        scheme_id: schemeId 
      });
      return res.data?.data || { states: [], gifts: [], slabs: [] };
    },
    enabled: !!schemeId,
    staleTime: 300000, // 5 minutes cache
  });
};